import { useEffect, useRef, useState } from "react";
import { isAxiosError } from "axios";
import { clsx } from "clsx";
import InfiniteScroll from "react-infinite-scroll-component";

import ListEmpty from "@/assets/images/Search_empty.png";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
} from "@/components/ui/breadcrumb";
import { useToast } from "@/components/ui/use-toast";
import { PAGE_SIZE_DEFAULT } from "@/constants/app.constants";
import { useAppSelector } from "@/redux/store";
import { getCustomMessage, getMessage } from "@/utils/message";

import { Information, Renew, RowDelete, TrashCan } from "@carbon/icons-react";
import { Loader2 } from "lucide-react";
import {
  checkExistName,
  getListSoftDelete,
  hardDeleteContent,
  restoreContent,
} from "../../api/lib.api";
import DialogConfirmDeleteContent from "../../components/DialogConfirmDeleteContent";
import ExistNameModal from "../../components/ExistNameModal";
import LibraryTable from "../../components/LibraryTable";
import {
  ContentTypeEnums,
  FolderTrashMenuActions,
} from "../../constants/lib.constants";
import { IContent } from "../../types/lib.types";
import FolderMenus from "./FolderMenus";

const menuItems = [
  {
    label: "Khôi phục",
    icon: Renew,
    key: FolderTrashMenuActions.Restore,
  },
  {
    label: "Thông tin",
    icon: Information,
    key: FolderTrashMenuActions.Detail,
  },
  {
    label: "Xóa vĩnh viễn",
    icon: RowDelete,
    key: FolderTrashMenuActions.Delete,
  },
  {
    label: "Xóa tất cả",
    icon: TrashCan,
    key: FolderTrashMenuActions.DeleteAll,
  },
];

const TrashList = () => {
  const { toast } = useToast();
  const containerRef = useRef<HTMLDivElement>(null);
  const { libraryId } = useAppSelector((state) => state.auth);

  const [contents, setContents] = useState<Partial<IContent>[]>([]);
  const pageIndex = 1;
  const totalPage = 1;
  const openContentDetailPopup = false;
  const [type, setType] = useState<FolderTrashMenuActions>(
    FolderTrashMenuActions.Restore
  );
  const [contentSelected, setContentSelected] = useState<Partial<IContent>>();
  const [isShowConfirmDeleteContent, setIsShowConfirmDeleteContent] =
    useState<boolean>(false);
  const [openExistName, setOpenExistName] = useState(false);
  const [newName, setNewName] = useState<string | null>("");
  //delete
  const onDelete = async () => {
    if (!contentSelected) return;
    try {
      if (!contentSelected.id || !contentSelected.name) return;
      await hardDeleteContent(contentSelected.id);
      toast({
        title: "Thành công!",
        variant: "success",
        description: (
          <div
            dangerouslySetInnerHTML={{
              __html: getCustomMessage("MSGTV36", contentSelected.name),
            }}
          ></div>
        ),
      });
    } catch (e: any) {
      toast({
        title: "Thất bại!",
        description: getMessage(e.data?.errors[0]),
      });
    } finally {
      setIsShowConfirmDeleteContent(false);
      fetchContents(pageIndex);
    }
  };
  const onDetail = async (content: Partial<IContent>) => {
    console.log("onDetail", content);
  };
  const onRestore = async () => {
    if (!contentSelected) return;
    try {
      if (!contentSelected.id || !contentSelected.name) return;
      const result = await checkExistName(
        contentSelected.name,
        contentSelected.size ?? 0,
        contentSelected.type ?? ContentTypeEnums.FILE,
        libraryId
      );
      if (result.data.duplicate) {
        setOpenExistName(true);
        setNewName(result.data.new_name);
      } else {
        await restoreContent(contentSelected.id);
        toast({
          title: "Thành công!",
          variant: "success",
          description: (
            <div
              dangerouslySetInnerHTML={{
                __html: getCustomMessage("MSGTV37", contentSelected.name),
              }}
            ></div>
          ),
        });
      }
    } catch (e: any) {
      toast({
        title: "Thất bại!",
        description: getMessage(e.data?.errors[0]),
      });
    } finally {
      setIsShowConfirmDeleteContent(false);
      fetchContents(pageIndex);
    }
  };

  const onRestoreConfirm = async () => {
    if (!contentSelected) return;
    setOpenExistName(false);
    try {
      if (!contentSelected.id || !contentSelected.name) return;
      await restoreContent(contentSelected.id);
      toast({
        title: "Thành công!",
        variant: "success",
        description: (
          <div
            dangerouslySetInnerHTML={{
              __html: getCustomMessage(
                "MSGTV37",
                newName ?? contentSelected.name
              ),
            }}
          ></div>
        ),
      });
    } catch (e: any) {
      toast({
        title: "Thất bại!",
        description: getMessage(e.data?.errors[0]),
      });
    } finally {
      setIsShowConfirmDeleteContent(false);
      fetchContents(pageIndex);
    }
  };

  const onDeleteAll = async (contents: Partial<IContent>[]) => {
    console.log("onDeleteAll", contents);
  };
  //Check action
  const onMenuClick = (
    action: FolderTrashMenuActions,
    content: Partial<IContent>
  ) => {
    setContentSelected(content);
    switch (action) {
      case FolderTrashMenuActions.DeSelect:
        handleDeSelect();
        break;
      case FolderTrashMenuActions.Detail:
        onDetail(content);
        break;
      case FolderTrashMenuActions.Restore:
        setType(FolderTrashMenuActions.Restore);
        setIsShowConfirmDeleteContent(true);
        break;
      case FolderTrashMenuActions.Delete:
        setType(FolderTrashMenuActions.Delete);
        setIsShowConfirmDeleteContent(true);
        break;
      case FolderTrashMenuActions.DeleteAll:
        onDeleteAll(contents);
        break;
      default:
        break;
    }
  };
  const handleDeSelect = () => {
    setContentSelected(undefined);
  };
  const fetchContents = async (pageNumber: number) => {
    try {
      const res = await getListSoftDelete({
        id: libraryId,
        pageNumber: pageNumber,
        pageSize: PAGE_SIZE_DEFAULT,
      });
      setContents(res.data);
    } catch (err) {
      if (isAxiosError(err)) {
        toast({
          title: "Thất bại!",
          variant: "error",
          description: getMessage(err.response?.data?.errors?.[0]),
        });
      }
    }
  };
  useEffect(() => {
    libraryId && fetchContents(pageIndex);
  }, [libraryId]);

  if (!libraryId) {
    return <></>;
  }
  return (
    <div className="container mx-auto">
      <div>
        {contents.length === 0 ? (
          <div className="flex items-center justify-center h-[calc(100vh-64px)]">
            <div className="flex flex-col items-center justify-center gap-6 max-w-[350px]">
              <img
                className="w-[124px]"
                src={ListEmpty}
                alt=""
              />
              <div className="text-center">
                <p className="text-base font-semibold mb-2">
                  Oops! Lưu trữ trống.
                </p>
                <p className="text-xs text-secondary-600">
                  Danh sách nội dung đã xoá sẽ được hiển thị tại đây.
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div>
            <Breadcrumb className="px-6 mt-7 mb-4">
              <BreadcrumbList>
                <BreadcrumbItem className="cursor-pointer">
                  <p className="text-secondary-600 text-sm font-semibold">
                    Lưu trữ
                  </p>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
            <div ref={containerRef}>
              <InfiniteScroll
                style={{
                  height: "unset",
                  overflow: "unset",
                }}
                className="px-0"
                dataLength={contents.length}
                next={() => {
                  fetchContents(pageIndex + 1);
                }}
                hasMore={pageIndex < totalPage}
                loader={
                  <div className="flex items-center justify-center mt-6">
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  </div>
                }
              >
                <div
                  className={clsx(
                    "flex px-4 flex-wrap",
                    openContentDetailPopup ? "w-[calc(100%-344px)]" : ""
                  )}
                >
                  {contentSelected && (
                    <FolderMenus
                      items={menuItems}
                      onClick={(action) => onMenuClick(action, contentSelected)}
                    />
                  )}
                  <LibraryTable
                    contents={contents as IContent[]}
                    contentSelected={contentSelected as IContent}
                    setContentSelected={setContentSelected}
                  />
                </div>
              </InfiniteScroll>
            </div>
          </div>
        )}
      </div>
      {contentSelected && (
        <DialogConfirmDeleteContent
          isShow={isShowConfirmDeleteContent}
          content={contentSelected}
          onAcceptContent={() => {
            switch (type) {
              case FolderTrashMenuActions.Restore:
                onRestore();
                break;
              case FolderTrashMenuActions.Delete:
                onDelete();
                break;
              default:
                break;
            }
          }}
          onRejectContent={() => setIsShowConfirmDeleteContent(false)}
          type={type}
        />
      )}
      <ExistNameModal
        open={openExistName}
        newName={newName}
        type="Restore"
        onSubmit={() => {
          onRestoreConfirm();
        }}
        onCancel={() => {
          setOpenExistName(false);
        }}
      />
    </div>
  );
};

export default TrashList;
